import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ROLES } from '../../data/roles';
import { dataFetch, authFetch } from '../../api';
import { sendEmail } from '../../services/resendemailService';
import { inviteAdminTemplate } from '../../email_templates/inviteAdmin';
import { useAuth } from '../../context/AuthContext';

const InviteAdminModal = ({ onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState(ROLES.ADMINISTRATOR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
    if (!emailList.length) {
      setError('Please enter at least one valid email address.');
      setLoading(false);
      return;
    }

    try {
      for (const email of emailList) {
        // Prevent duplicate pending
        const existRes = await dataFetch(`read/admin_invites?email=${encodeURIComponent(email)}&status=Pending`);
        if (existRes?.data?.length > 0) continue;

        const firstName = email.split('@')[0];
        const sixDigitCode = Math.floor(100000 + Math.random() * 900000).toString();
        const tempPassword = `${firstName}@${sixDigitCode}!`;

        // Create User in Auth system WITHOUT touching admin session (credentials: 'omit')
        const authRes = await authFetch('sign-up/email', {
          method: 'POST',
          body: JSON.stringify({ 
            email: email, 
            password: tempPassword, 
            name: firstName
          }),
          credentials: 'omit'
        });

        if (authRes?.status !== 'success') {
          setError(`Failed to create account for ${email}: ${authRes?.error}`);
          continue;
        }

        const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        const expDate = new Date();
        expDate.setHours(expDate.getHours() + 72);
        
        const currentUserId = String(currentUser?.id || localStorage.getItem('user_id') || '');

        const createRes = await dataFetch('create/admin_invites', {
          method: 'POST',
          body: JSON.stringify({
            email,
            role,
            token,
            status: 'Pending',
            // Format to YYYY-MM-DD HH:mm:ss for DB compatibility
            expires_at: expDate.toISOString().slice(0, 19).replace('T', ' '),
            created_by: currentUserId,
            user_id: String(authRes.data.user.id),
            temp_password: tempPassword
          })
        });

        if (createRes?.status === 'success') {
          // --- NEW LOGIC: Save to Employee Table ---
          const existEmpRes = await dataFetch(`read/employees?email=${encodeURIComponent(email)}`);
          if (existEmpRes?.data?.length > 0) {
            // Update existing employee record to admin role
            await dataFetch(`update/employees/${existEmpRes.data[0].id}`, {
              method: 'PUT',
              body: JSON.stringify({
                ...existEmpRes.data[0],
                role: role,
                inviteStatus: 'Invited',
                temp_password: tempPassword,
                user_id: String(authRes.data.user.id)
              })
            });
          } else {
            // Create new employee record with selected admin role
            await dataFetch('create/employees', {
              method: 'POST',
              body: JSON.stringify({
                email: email,
                firstName: firstName,
                lastName: 'Admin', // Default placeholder
                department: 'Administration',
                jobTitle: role,
                role: role,
                inviteStatus: 'Invited',
                inviteDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
                temp_password: tempPassword,
                user_id: String(authRes.data.user.id),
                employmentStatus: 'Active',
                phishingRiskScore: 0,
                trainingStatus: 'Not Started',
                manager: 'N/A',
                location: 'Remote'
              })
            });
          }

          const inviteLink = `${window.location.origin}/login`;
          
          // Send Email using Resend
          const emailResult = await sendEmail({
             to: email,
             subject: 'Invitation to Join Sentinel Admin Platform',
             html: inviteAdminTemplate({
               name: firstName,
               email: email,
               role: role,
               tempPassword: tempPassword,
               loginUrl: inviteLink
             }),
             from: "noreply@sentinel.biscontractors.com"
          });

          if (!emailResult.success) {
             console.warn('Invite created, but failed to send email via Resend:', emailResult.error);
          }

          // Log Audit Event
          await dataFetch('create/audit_logs', {
              method: 'POST',
              body: JSON.stringify({
                  action: 'Admin Invite Sent',
                  details: `Invited ${email} as ${role}`,
                  user_id: currentUserId,
                  created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
              })
          });
        } else {
           setError(`Failed to save invite record for ${email}`);
        }
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      setError('An error occurred while sending invites.');
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }} 
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">Invite Administrator</h3>
            <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-50 rounded-lg">
              <SafeIcon icon={FiIcons.FiX} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Addresses</label>
              <textarea 
                value={emails}
                onChange={e => setEmails(e.target.value)}
                className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                placeholder="admin@company.com, manager@company.com"
                rows="3"
                required
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple emails with commas.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select 
                value={role}
                onChange={e => setRole(e.target.value)}
                className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-slate-900 outline-none bg-white"
              >
                {Object.values(ROLES)
                  .filter(r => r !== ROLES.EMPLOYEE)
                  .map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="pt-4 flex justify-end gap-3">
              <button 
                type="button" 
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />}
                Send Invitations
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default InviteAdminModal;